import { useCallback, useEffect, useMemo, useState } from 'react'
import { Compass, Database, Link2, RefreshCw, Sparkles } from 'lucide-react'
import { getBookLogo } from '../lib/logoMaps'
import {
  CORE_CLOUD_TARGETS,
  EXPANSION_LAST_UPDATED,
  ISOLATED_PROPS_BOOKS,
  ISOLATED_TEAM_BOOKS,
  MARKET_IDENTITY_RULES,
  NEXT_TARGETS,
  PROPS_MATCHING_PROOF,
  TEAM_MATCHING_PROOF,
  THEODDSAPI_REGION_SUMMARY,
  PENDING_CORE_TARGETS,
} from '../data/bookCoverage'
import './ExpansionCoverageWidget.css'

const VM_IP = '170.9.62.134'
const VM_PORT = 8443
const DIRECT_VM_BASE = `http://${VM_IP}:${VM_PORT}`
const PROXY_PATH = '/api/oracle-vm'

function resolveEndpointUrl(endpoint) {
  if (typeof window !== 'undefined' && window.location.protocol === 'http:') {
    return `${DIRECT_VM_BASE}/${endpoint}`
  }
  return `${PROXY_PATH}?endpoint=${encodeURIComponent(endpoint)}`
}

async function fetchJsonWithTimeout(endpoint, timeoutMs = 5000) {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs)

  try {
    const response = await fetch(resolveEndpointUrl(endpoint), {
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    })
    const data = await response.json().catch(() => null)
    if (!response.ok) {
      throw new Error(data?.error || `HTTP ${response.status}`)
    }
    return data
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Timed out after 5 seconds')
    }
    throw error
  } finally {
    clearTimeout(timeoutId)
  }
}

function formatBookLabel(rawKey) {
  return String(rawKey || '')
    .replace(/^abet_scraper_/, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString()
}

export default function ExpansionCoverageWidget() {
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lastUpdated, setLastUpdated] = useState(null)

  const fetchStats = useCallback(async () => {
    setLoading(true)
    try {
      const data = await fetchJsonWithTimeout('stats')
      setStats(data)
      setError(null)
      setLastUpdated(new Date())
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchStats()
    const interval = setInterval(fetchStats, 45000)
    return () => clearInterval(interval)
  }, [fetchStats])

  const productionBooks = useMemo(() => {
    return Object.entries(stats?.by_bookmaker || {})
      .map(([bookmaker, count]) => ({ bookmaker: formatBookLabel(bookmaker), count: Number(count) || 0 }))
      .sort((a, b) => b.count - a.count)
  }, [stats])

  const productionCount = productionBooks.length
  const isolatedBuiltCount = ISOLATED_TEAM_BOOKS.filter((book) => !book.status.toLowerCase().includes('partial')).length + ISOLATED_PROPS_BOOKS.length
  const teamExactMatchPairs = TEAM_MATCHING_PROOF.reduce((sum, row) => sum + row.exactKeys, 0)
  const propsExactMatchPairs = PROPS_MATCHING_PROOF.reduce((sum, row) => sum + row.exactMatches, 0)

  return (
    <div className="widget ecw-widget">
      <div className="widget-header">
        <h3 className="widget-title">
          <Compass size={16} />
          Expansion Coverage
          <span className="ecw-badge">{CORE_CLOUD_TARGETS.length} core targets</span>
        </h3>
        <button className="widget-refresh-btn" onClick={fetchStats} title="Refresh production stats">
          <RefreshCw size={14} className={loading ? 'ecw-spinner' : ''} />
        </button>
      </div>

      <div className="ecw-summary">
        <div className="ecw-stat-card">
          <span className="ecw-stat-value">{productionCount}</span>
          <span className="ecw-stat-label">Production books active now</span>
        </div>
        <div className="ecw-stat-card">
          <span className="ecw-stat-value">{ISOLATED_TEAM_BOOKS.length}</span>
          <span className="ecw-stat-label">Isolated team books built</span>
        </div>
        <div className="ecw-stat-card">
          <span className="ecw-stat-value">{ISOLATED_PROPS_BOOKS.length}</span>
          <span className="ecw-stat-label">Isolated props books built</span>
        </div>
        <div className="ecw-stat-card">
          <span className="ecw-stat-value">{isolatedBuiltCount}</span>
          <span className="ecw-stat-label">New books proven outside prod</span>
        </div>
      </div>

      <div className="ecw-callout">
        <Sparkles size={14} />
        <span>
          Production coverage comes from the live Oracle receiver. Expansion coverage comes from isolated scrapers with proof outputs on disk, so we can keep building without stepping on the live VM stack.
        </span>
      </div>

      {error && <div className="ecw-error">{error}</div>}

      <div className="ecw-grid">
        <section className="ecw-panel">
          <div className="ecw-panel-header">
            <span>Production Receiver Coverage</span>
            <span className="ecw-panel-meta">
              {stats?.total_odds != null ? `${formatNumber(stats.total_odds)} odds in memory` : 'waiting for stats'}
            </span>
          </div>
          {productionBooks.length === 0 ? (
            <div className="ecw-empty">No live receiver bookmaker stats available yet.</div>
          ) : (
            <div className="ecw-list">
              {productionBooks.map((book) => (
                <div key={book.bookmaker} className="ecw-row">
                  <div className="ecw-row-name">
                    {getBookLogo(book.bookmaker) && (
                      <img
                        src={getBookLogo(book.bookmaker)}
                        alt=""
                        className="ecw-book-logo"
                        onError={(event) => { event.target.style.display = 'none' }}
                      />
                    )}
                    <span>{book.bookmaker}</span>
                  </div>
                  <span className="ecw-row-value">{formatNumber(book.count)}</span>
                </div>
              ))}
            </div>
          )}
          <div className="ecw-footnote">
            {lastUpdated ? `Production stats refreshed ${lastUpdated.toLocaleTimeString()}` : 'Waiting for first refresh'}
          </div>
        </section>

        <section className="ecw-panel">
          <div className="ecw-panel-header">
            <span>Core Cloud Targets</span>
            <span className="ecw-panel-meta">Current ABET 16-book stack</span>
          </div>
          <div className="ecw-chip-grid">
            {CORE_CLOUD_TARGETS.map((book) => (
              <div key={book.bookmaker} className="ecw-chip-card">
                <div className="ecw-chip-title">
                  {getBookLogo(book.bookmaker) && (
                    <img
                      src={getBookLogo(book.bookmaker)}
                      alt=""
                      className="ecw-book-logo"
                      onError={(event) => { event.target.style.display = 'none' }}
                    />
                  )}
                  <span>{book.bookmaker}</span>
                </div>
                <span className="ecw-chip-status">{book.status}</span>
                <span className="ecw-chip-meta">{book.lane} · {book.type}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="ecw-panel">
          <div className="ecw-panel-header">
            <span>Isolated Team-Sport Books</span>
            <span className="ecw-panel-meta">Outcome[] ready</span>
          </div>
          <div className="ecw-proof-list">
            {ISOLATED_TEAM_BOOKS.map((book) => (
              <div key={book.bookmaker} className="ecw-proof-card">
                <div className="ecw-proof-top">
                  <div className="ecw-proof-title">
                    {getBookLogo(book.bookmaker) && (
                      <img
                        src={getBookLogo(book.bookmaker)}
                        alt=""
                        className="ecw-book-logo"
                        onError={(event) => { event.target.style.display = 'none' }}
                      />
                    )}
                    <span>{book.bookmaker}</span>
                  </div>
                  <span className={`ecw-proof-status ${book.status.toLowerCase().includes('partial') ? 'blocked' : 'live'}`}>{book.status}</span>
                </div>
                <div className="ecw-proof-text">{book.proof}</div>
                <div className="ecw-proof-meta">{book.path}</div>
                <div className="ecw-proof-note">{book.note}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="ecw-panel">
          <div className="ecw-panel-header">
            <span>Isolated Props / Pick’em Books</span>
            <span className="ecw-panel-meta">Projection lane</span>
          </div>
          <div className="ecw-proof-list">
            {ISOLATED_PROPS_BOOKS.map((book) => (
              <div key={book.bookmaker} className="ecw-proof-card">
                <div className="ecw-proof-top">
                  <div className="ecw-proof-title">
                    {getBookLogo(book.bookmaker) && (
                      <img
                        src={getBookLogo(book.bookmaker)}
                        alt=""
                        className="ecw-book-logo"
                        onError={(event) => { event.target.style.display = 'none' }}
                      />
                    )}
                    <span>{book.bookmaker}</span>
                  </div>
                  <span className="ecw-proof-status live">{book.status}</span>
                </div>
                <div className="ecw-proof-text">{book.proof}</div>
                <div className="ecw-proof-meta">{book.path}</div>
                <div className="ecw-proof-note">{book.note}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="ecw-panel">
          <div className="ecw-panel-header">
            <span>Same-Bet Matching Proof</span>
            <span className="ecw-panel-meta">OddsJam-style join keys</span>
          </div>
          <div className="ecw-match-summary">
            <div className="ecw-match-stat">
              <Link2 size={14} />
              <span>{teamExactMatchPairs} team exact-key overlaps</span>
            </div>
            <div className="ecw-match-stat">
              <Database size={14} />
              <span>{propsExactMatchPairs} props exact same-line matches</span>
            </div>
          </div>
          <div className="ecw-rule-list">
            {MARKET_IDENTITY_RULES.map((rule) => (
              <div key={`${rule.lane}-${rule.key}`} className="ecw-rule-card">
                <div className="ecw-rule-title">{rule.lane}</div>
                <div className="ecw-rule-key">{rule.key}</div>
                <div className="ecw-rule-detail">{rule.detail}</div>
              </div>
            ))}
          </div>
          <div className="ecw-overlap-grid">
            {TEAM_MATCHING_PROOF.map((row) => (
              <div key={row.pair} className="ecw-overlap-card">
                <div className="ecw-overlap-title">{row.pair}</div>
                <div className="ecw-overlap-metric">{row.sharedEvents} shared events · {row.exactKeys} exact keys</div>
                <div className="ecw-overlap-note">{row.note}</div>
              </div>
            ))}
            {PROPS_MATCHING_PROOF.map((row) => (
              <div key={row.pair} className="ecw-overlap-card">
                <div className="ecw-overlap-title">{row.pair}</div>
                <div className="ecw-overlap-metric">{row.sharedGroups} shared groups · {row.exactMatches} exact matches</div>
                <div className="ecw-overlap-note">{row.note}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="ecw-panel">
          <div className="ecw-panel-header">
            <span>Pending Core Fixes</span>
            <span className="ecw-panel-meta">Current cloud blockers</span>
          </div>
          <div className="ecw-proof-list">
            {PENDING_CORE_TARGETS.map((row) => (
              <div key={row.bookmaker} className="ecw-proof-card">
                <div className="ecw-proof-top">
                  <div className="ecw-proof-title">
                    {getBookLogo(row.bookmaker) && (
                      <img
                        src={getBookLogo(row.bookmaker)}
                        alt=""
                        className="ecw-book-logo"
                        onError={(event) => { event.target.style.display = 'none' }}
                      />
                    )}
                    <span>{row.bookmaker}</span>
                  </div>
                </div>
                <div className="ecw-proof-note">{row.blocker}</div>
                <div className="ecw-proof-meta">{row.nextStep}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="ecw-panel">
          <div className="ecw-panel-header">
            <span>Global Target Registry</span>
            <span className="ecw-panel-meta">TheOddsAPI-aligned scope</span>
          </div>
          <div className="ecw-region-grid">
            {THEODDSAPI_REGION_SUMMARY.map((row) => (
              <div key={row.region} className="ecw-region-card">
                <div className="ecw-region-count">{row.count}</div>
                <div className="ecw-region-label">{row.region}</div>
              </div>
            ))}
          </div>
          <div className="ecw-target-list">
            {NEXT_TARGETS.map((target) => (
              <div key={target.bookmaker} className="ecw-target-chip">
                <span className="ecw-target-name">{target.bookmaker}</span>
                <span className="ecw-target-meta">{target.type}</span>
                <span className="ecw-target-blocker">{target.blocker}</span>
              </div>
            ))}
          </div>
        </section>
      </div>

      <div className="widget-footer">
        <span>Expansion lab status updated {EXPANSION_LAST_UPDATED}</span>
        <span>Use this tab to distinguish production-live books from isolated-lab books</span>
      </div>
    </div>
  )
}
